import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useEvents } from '../context/EventsContext';
import { formatDate, formatPrice } from '../utils/format';
import { 
  ArrowLeft, Calendar, Clock, Users, 
  Check, X, Armchair
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import ReservationForm from '../components/custom/ReservationForm';

import { getChairPositionsWithOverrides } from '../utils/chairPositions';

const FloorPlan = ({ layout, selectedChairs, onToggleChair }) => {
  const chairs = layout.chairs || [];
  const tables = layout.tables || [];

  // Group chairs by table
  const chairsByTable = {};
  chairs.forEach(c => {
    if (!chairsByTable[c.tableId]) chairsByTable[c.tableId] = [];
    chairsByTable[c.tableId].push(c);
  });

  return (
    <div 
      data-testid="floor-plan"
      className="relative w-full bg-secondary/50 rounded-lg border border-border overflow-hidden"
      style={{ paddingBottom: '65%' }}
    >
      {/* Grid */}
      <div 
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: `
            linear-gradient(rgba(234, 88, 12, 0.03) 1px, transparent 1px),
            linear-gradient(90deg, rgba(234, 88, 12, 0.03) 1px, transparent 1px)
          `,
          backgroundSize: '20px 20px'
        }}
      />

      {/* Features */}
      {(layout.features || []).map((feature, index) => (
        <div
          key={`feature-${index}`}
          className="absolute flex items-center justify-center bg-muted/50 border border-border rounded text-xs text-muted-foreground"
          style={{
            left: `${feature.x}%`,
            top: `${feature.y}%`,
            width: `${feature.width}%`,
            height: `${feature.height}%`
          }}
        >
          {feature.label}
        </div>
      ))}

      {/* Tables (visual only) */}
      {tables.map((table) => (
        <div
          key={table.id}
          className={`absolute flex items-center justify-center border border-border/60 bg-muted/20 text-muted-foreground/60
            ${table.shape === 'circle' ? 'rounded-full' : 'rounded-lg'}`}
          style={{
            left: `${table.x}%`,
            top: `${table.y}%`,
            width: `${table.width}%`,
            height: `${table.height}%`
          }}
        >
          <span className="text-[10px] font-medium">{table.label}</span>
        </div>
      ))}

      {/* Chairs */}
      {tables.map((table) => {
        const tableChairs = chairsByTable[table.id] || [];
        const positions = getChairPositionsWithOverrides(table, tableChairs);

        return tableChairs.map((chair, idx) => {
          const pos = positions[idx];
          if (!pos) return null;
          const isSelected = selectedChairs.includes(chair.id);
          const isAvailable = chair.status === 'available';

          return (
            <button
              key={chair.id}
              data-testid={`chair-${chair.id}`}
              onClick={() => isAvailable && onToggleChair(chair)}
              disabled={!isAvailable}
              className={`absolute w-[3%] h-[4%] rounded-full flex items-center justify-center transition-all duration-200
                ${isAvailable
                  ? isSelected
                    ? 'bg-primary border-2 border-primary shadow-[0_0_12px_rgba(234,88,12,0.6)] scale-125 z-10'
                    : 'bg-emerald-900/60 border border-emerald-600/60 hover:border-emerald-400 hover:scale-110 cursor-pointer'
                  : 'bg-muted/40 border border-border/50 cursor-not-allowed opacity-50'
                }`}
              style={{ left: `${pos.x}%`, top: `${pos.y}%` }}
              title={`${chair.label} - ${isAvailable ? `$${chair.price}` : 'Reservada'}`}
            >
              <span className="text-[7px] font-bold text-white/90">{chair.label.slice(-1)}</span>
            </button>
          );
        });
      })}
    </div>
  );
};

const EventPage = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { getEventDetail, getEventPaymentMethods } = useEvents();
  
  const [event, setEvent] = useState(null);
  const [layout, setLayout] = useState({ tables: [], chairs: [], features: [] });
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loadingEvent, setLoadingEvent] = useState(true);
  const [selectedChairs, setSelectedChairs] = useState([]);
  const [showReservationModal, setShowReservationModal] = useState(false);
  const [reservationSuccess, setReservationSuccess] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const data = await getEventDetail(eventId);
        setEvent(data);
        setLayout(data.layout || { tables: [], chairs: [], features: [] });
        const pms = await getEventPaymentMethods(eventId);
        setPaymentMethods(pms || []);
      } catch {
        setEvent(null);
      } finally {
        setLoadingEvent(false);
      }
    };
    load();
  }, [eventId, getEventDetail, getEventPaymentMethods]);

  if (loadingEvent) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <p className="text-muted-foreground">Cargando evento...</p>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-center">
          <p className="text-muted-foreground mb-4">Evento no encontrado</p>
          <Button onClick={() => navigate('/')} variant="outline">Volver al inicio</Button>
        </div>
      </div>
    );
  }

  const chairs = layout.chairs || [];
  const selectedChairObjects = chairs.filter(c => selectedChairs.includes(c.id));
  const totalPrice = selectedChairObjects.reduce((sum, c) => sum + (parseFloat(c.price) || 0), 0);

  const handleToggleChair = (chair) => {
    setSelectedChairs(prev => 
      prev.includes(chair.id) 
        ? prev.filter(id => id !== chair.id)
        : [...prev, chair.id]
    );
  };

  const handleReserve = () => {
    if (selectedChairs.length > 0) {
      setShowReservationModal(true);
    }
  };

  const handleReservationSuccess = () => {
    setReservationSuccess(true);
    setShowReservationModal(false);
    setSelectedChairs([]);
  };

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Header */}
      <header className="border-b border-border bg-secondary/50 sticky top-0 z-30 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            data-testid="back-btn"
            onClick={() => navigate('/')}
            className="text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Eventos
          </Button>
          <div className="flex-1">
            <h1 className="text-lg font-serif font-semibold text-foreground">{event.name}</h1>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-6">
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Event Info */}
          <div className="lg:col-span-1 space-y-4">
            {/* Image */}
            <div className="relative rounded-lg overflow-hidden">
              <img src={event.image} alt={event.name} className="w-full h-48 object-cover" />
              <Badge className={`absolute top-3 right-3 ${
                event.status === 'open' 
                  ? 'bg-emerald-500/90 text-white' 
                  : 'bg-muted text-muted-foreground'
              }`}>
                {event.status === 'open' ? 'Disponible' : 'Agotado'}
              </Badge>
            </div>

            {/* Details */}
            <div className="bg-secondary border border-border rounded-lg p-4 space-y-3">
              <p className="text-sm text-muted-foreground">{event.description}</p>
              <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
                <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" />{formatDate(event.date)}</span>
                <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" />{event.time} hrs</span>
                <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5" />{chairs.filter(c => c.status === 'available').length} sillas disponibles</span>
              </div>
              {event.priceFrom > 0 && (
                <p className="text-sm text-primary font-medium">Desde {formatPrice(event.priceFrom, event.currency)}</p>
              )}
            </div>

            {/* Legend */}
            <div className="bg-secondary border border-border rounded-lg p-4 space-y-2">
              <h4 className="text-xs font-medium text-foreground">Leyenda</h4>
              <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
                <span className="flex items-center gap-1.5">
                  <span className="w-3 h-3 rounded-full bg-emerald-900/60 border border-emerald-600/60" /> Disponible
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="w-3 h-3 rounded-full bg-primary border-2 border-primary" /> Seleccionada
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="w-3 h-3 rounded-full bg-muted/40 border border-border/50 opacity-50" /> Reservada
                </span>
              </div>
            </div>

            {/* Selection Summary */}
            {selectedChairs.length > 0 && (
              <div data-testid="selection-summary" className="bg-secondary border border-primary/30 rounded-lg p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-medium text-foreground flex items-center gap-2">
                    <Armchair className="w-4 h-4 text-primary" />
                    {selectedChairs.length} silla{selectedChairs.length > 1 ? 's' : ''} seleccionada{selectedChairs.length > 1 ? 's' : ''}
                  </h4>
                  <button
                    onClick={() => setSelectedChairs([])}
                    className="text-xs text-muted-foreground hover:text-foreground"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <div className="space-y-1 max-h-32 overflow-y-auto">
                  {selectedChairObjects.map(chair => {
                    const table = (layout.tables || []).find(t => t.id === chair.tableId);
                    return (
                      <div key={chair.id} className="flex justify-between text-xs">
                        <span className="text-muted-foreground">{table?.label} · Silla {chair.label}</span>
                        <span className="text-foreground">{formatPrice(chair.price, event.currency)}</span>
                      </div>
                    );
                  })}
                </div>
                <div className="border-t border-border pt-2 flex justify-between items-center">
                  <span className="text-sm font-medium text-foreground">Total</span>
                  <span className="text-lg font-bold text-primary">{formatPrice(totalPrice, event.currency)}</span>
                </div>
                <Button
                  data-testid="reserve-btn"
                  onClick={handleReserve}
                  className="w-full bg-primary hover:bg-accent glow-orange"
                >
                  <Check className="w-4 h-4 mr-2" />
                  Reservar Sillas
                </Button>
              </div>
            )}

            {/* Success */}
            {reservationSuccess && (
              <div data-testid="success-message" className="p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-lg text-center">
                <Check className="w-8 h-8 mx-auto text-emerald-400 mb-2" />
                <p className="text-sm font-medium text-emerald-400">Reservación enviada</p>
                <p className="text-xs text-muted-foreground mt-1">Te contactaremos pronto</p>
              </div>
            )}
          </div>

          {/* Floor Plan */}
          <div className="lg:col-span-2">
            <div className="sticky top-16">
              <h3 className="text-sm font-medium text-foreground mb-3">
                Selecciona tus sillas — toca las disponibles
              </h3>
              <FloorPlan
                layout={layout}
                selectedChairs={selectedChairs}
                onToggleChair={handleToggleChair}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Reservation Modal */}
      <Dialog open={showReservationModal} onOpenChange={setShowReservationModal}>
        <DialogContent className="sm:max-w-lg bg-[#0a0a0a] border-border max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="text-xl font-serif">Completar Reservación</DialogTitle>
            <DialogDescription>
              {event.name} — {selectedChairs.length} silla{selectedChairs.length > 1 ? 's' : ''}
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto pr-2 -mr-2">
            <ReservationForm
              event={event}
              chairs={selectedChairObjects}
              tables={layout.tables || []}
              totalPrice={totalPrice}
              paymentMethods={paymentMethods}
              onSuccess={handleReservationSuccess}
              onCancel={() => setShowReservationModal(false)}
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EventPage;
